import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Tooltip,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import Spinner from 'src/components/Spinner/Spinner';
import PerfectScrollbarTable from 'src/components/PerfectScrollbarTable';
import {
  setImportPBTemplatesModalVisible,
  getPBTemplates,
  setSelectedPBTemplates,
  importPBTemplates,
  getMessages,
} from 'src/actions/messagesActions';
import importPBTemplatesModalStyle from './importPBTemplatesModal.style';

function ImportPBTemplatesModal({
  classes,
  currentTemplateCount,
}) {
  const dispatch = useDispatch();
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);

  const {
    importPBTemplatesModalVisible,
    pbTemplates,
    pbTemplatesLoading,
    selectedPBTemplates,
    importPBTemplatesLoading,
  } = useSelector((state) => state.messages);

  const isVisible = importPBTemplatesModalVisible;
  const hasTemplates = pbTemplates && pbTemplates.length > 0;
  const hasSelectedTemplates = selectedPBTemplates && selectedPBTemplates.length > 0;
  const isAllSelected = hasTemplates && selectedPBTemplates.length === pbTemplates.length;
  const isSomeSelected = hasSelectedTemplates && selectedPBTemplates.length < pbTemplates.length;

  useEffect(() => {
    if (isVisible && !pbTemplatesLoading) {
      dispatch(getPBTemplates());
    }
  }, [isVisible, dispatch]);

  const handleClose = () => {
    dispatch(setImportPBTemplatesModalVisible(false));
    setIsConfirmVisible(false);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      dispatch(setSelectedPBTemplates([]));
    } else {
      dispatch(setSelectedPBTemplates(pbTemplates.map((template) => template._id))); // eslint-disable-line no-underscore-dangle
    }
  };

  const handleSelectTemplate = (templateId) => {
    const currentSelected = [...selectedPBTemplates];
    const index = currentSelected.indexOf(templateId);

    if (index > -1) {
      currentSelected.splice(index, 1);
    } else {
      currentSelected.push(templateId);
    }

    dispatch(setSelectedPBTemplates(currentSelected));
  };

  const handleRowClick = (templateId) => {
    handleSelectTemplate(templateId);
  };

  const handleImportClick = () => {
    if (hasSelectedTemplates) {
      setIsConfirmVisible(true);
    }
  };

  const handleConfirmImport = () => {
    const doWhenTry = () => {
      dispatch(getMessages());
      handleClose();
    };

    dispatch(importPBTemplates(selectedPBTemplates, doWhenTry));
    setIsConfirmVisible(false);
  };

  const truncatePreviewText = (text, maxLength = 200) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const truncateTemplateName = (name, maxLength = 100) => {
    if (!name) return 'Untitled Template';
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };

  const renderTooltipContent = (templateName, templateText, template) => {
    // Get images from the template's media array
    const templateImages = template?.media && Array.isArray(template.media)
      ? template.media.filter((url) => typeof url === 'string' && url.trim() !== '')
      : [];

    // Also check for any image URLs in the text as backup
    const imageUrlRegex = /(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp))/gi;
    const textImageUrls = templateText ? templateText.match(imageUrlRegex) || [] : [];

    // Combine and deduplicate images, limit to 3
    const allImages = [...new Set([...templateImages, ...textImageUrls])].slice(0, 3);

    return (
      <Box className={classes.tooltipContent}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          {templateName}
        </Typography>
        {allImages.length > 0 && (
          <Box sx={{ marginBottom: 1 }}>
            {allImages.map((imageUrl) => (
              <img
                key={imageUrl}
                src={imageUrl}
                alt=""
                style={{
                  maxWidth: '100px',
                  height: 'auto',
                  marginBottom: allImages.indexOf(imageUrl) < allImages.length - 1 ? 8 : 0,
                  borderRadius: 4,
                  maxHeight: 100,
                  display: 'block'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ))}
          </Box>
        )}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {templateText}
        </Typography>
      </Box>
    );
  };

  const templatesFoundText = hasTemplates
    ? `${pbTemplates.length} Template${pbTemplates.length === 1 ? '' : 's'} found in your Project Broadcast account`
    : 'No templates found in your Project Broadcast account';

  const selectedText = hasSelectedTemplates
    ? `${selectedPBTemplates.length} template${selectedPBTemplates.length === 1 ? '' : 's'} selected`
    : 'No templates selected';

  const note = 'After import, please edit templates to remove PB dynamic fields or replace with CP dynamic fields, such as {{firstName}} → [First Name] or {{custom1}} → something else, otherwise they will be blank when a message is sent.';

  const MAX_USER_TEMPLATES = 999;
  const availableSlots = MAX_USER_TEMPLATES - currentTemplateCount;
  const selectedCount = selectedPBTemplates.length;
  const wouldExceedLimit = selectedCount > availableSlots;
  const templatesOverLimit = selectedCount - availableSlots;

  return (
    <>
      <ModalDialogMUI6
        isVisible={isVisible && !isConfirmVisible}
        title="Import Project Broadcast Templates"
        okText="Import Selected Templates"
        onClose={handleClose}
        onOk={handleImportClick}
        closeText="Cancel"
        okButtonProps={{
          disabled: !hasSelectedTemplates || pbTemplatesLoading || importPBTemplatesLoading || wouldExceedLimit || !hasTemplates,
        }}
        loading={pbTemplatesLoading || importPBTemplatesLoading}
        largeModal
        content={(
          <Box className={classes.container}>
            <Spinner
              loading={pbTemplatesLoading}
              positionType="absolute"
            />

            {/* Only show note boxes if there are templates */}
            {hasTemplates && (
              <Box
                sx={{
                  backgroundColor: '#fff3cd', // Light yellow
                  padding: 2,
                  borderRadius: 1,
                  marginBottom: 2,
                  border: '1px solid #ffeaa7' // Yellow border
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  <strong>Note:</strong>
                  {' '}
                  {note}
                </Typography>
              </Box>
            )}

            {/* Show different content based on whether templates exist */}
            {hasTemplates ? (
              <>
                <Box className={classes.headerSection}>
                  <Typography className={classes.templateFoundText}>
                    {templatesFoundText}
                  </Typography>

                  <Typography className={classes.selectedText}>
                    {selectedText}
                  </Typography>
                </Box>

                <Paper className={classes.tableContainer} elevation={0}>
                  <PerfectScrollbarTable maxHeight={400}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell className={`${classes.headerCell} ${classes.selectHeaderCell}`}>
                            <Checkbox
                              className={classes.selectAllHeaderCheckbox}
                              checked={isAllSelected}
                              indeterminate={isSomeSelected}
                              onChange={handleSelectAll}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell className={`${classes.headerCell} ${classes.nameHeaderCell}`}>
                            Template Name
                          </TableCell>
                          <TableCell className={`${classes.headerCell} ${classes.previewHeaderCell}`}>
                            Text Preview
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pbTemplates.map((template) => {
                          const isSelected = selectedPBTemplates.includes(template._id); // eslint-disable-line no-underscore-dangle
                          const templateName = template.name || 'Untitled Template';
                          const templateText = template.text || '';
                          const displayName = truncateTemplateName(templateName);
                          const displayText = truncatePreviewText(templateText);

                          return (
                            <TableRow
                              key={template._id} // eslint-disable-line no-underscore-dangle
                              className={classes.templateRow}
                              onClick={() => handleRowClick(template._id)} // eslint-disable-line no-underscore-dangle
                              selected={isSelected}
                              hover
                            >
                              <TableCell className={classes.selectCell}>
                                <Checkbox
                                  className={classes.rowCheckbox}
                                  checked={isSelected}
                                  onChange={() => handleSelectTemplate(template._id)} // eslint-disable-line no-underscore-dangle
                                  color="primary"
                                  size="small"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell className={classes.nameCell}>
                                <Tooltip
                                  title={renderTooltipContent(templateName, templateText, template)}
                                  placement="top-start"
                                  arrow
                                  classes={{ tooltip: classes.customTooltip }}
                                >
                                  <Typography
                                    className={classes.templateName}
                                  >
                                    {displayName}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell className={classes.previewCell}>
                                <Tooltip
                                  title={renderTooltipContent(templateName, templateText, template)}
                                  placement="top-start"
                                  arrow
                                  classes={{ tooltip: classes.customTooltip }}
                                >
                                  <Typography
                                    className={classes.templatePreview}
                                  >
                                    {displayText}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </PerfectScrollbarTable>
                </Paper>

                {/* Bottom note box */}
                <Box
                  sx={{
                    backgroundColor: '#fff3cd', // Light yellow
                    padding: 2,
                    borderRadius: 1,
                    marginBottom: 2,
                    marginTop: 2,
                    border: '1px solid #ffeaa7' // Yellow border
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    <strong>Note:</strong>
                    {' '}
                    {note}
                  </Typography>
                </Box>

                {/* Warning box for exceeding limits */}
                {wouldExceedLimit && (
                  <Box
                    sx={{
                      backgroundColor: '#ffebee', // Light red
                      padding: 2,
                      borderRadius: 1,
                      marginTop: 2,
                      border: '1px solid #f44336' // Red border
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#d32f2f' }}>
                      <strong>Warning:</strong>
                      You have selected
                      {' '}
                      {selectedCount}
                      {' '}
                      templates, but you only have
                      {' '}
                      {availableSlots}
                      {' '}
                      available templates to create. Please deselect
                      {' '}
                      {templatesOverLimit}
                      {' '}
                      template
                      {templatesOverLimit === 1 ? '' : 's'}
                      {' to proceed. (Current: '}
                      {currentTemplateCount}
                      {'/999, Available: '}
                      {availableSlots}
                      {' )'}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              /* Clean "no templates" state */
              !pbTemplatesLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    minHeight: 300,
                    padding: 4,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                      marginBottom: 2,
                    }}
                  >
                    No Templates Available
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1rem',
                      color: 'text.secondary',
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    You don't have any templates in your Project Broadcast account to import.
                    Templates you create in Project Broadcast will appear here for importing.
                  </Typography>
                </Box>
              )
            )}
          </Box>
        )}
      />

      <ModalDialogMUI6
        isVisible={isConfirmVisible}
        title="Confirm Import"
        onClose={() => setIsConfirmVisible(false)}
        onOk={handleConfirmImport}
        okText="Yes, Import Templates"
        closeText="Cancel"
        loading={importPBTemplatesLoading}
        content={(
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              p: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Are you sure you want to import
              {' '}
              {selectedPBTemplates.length}
              {' '}
              template
              {selectedPBTemplates.length === 1 ? '' : 's'}
              {' '}
              from Project Broadcast?
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 460, lineHeight: 1.6 }}
            >
              This will create
              {selectedPBTemplates.length === 1 ? ' a ' : ' '}
              new message template
              {selectedPBTemplates.length === 1 ? '' : 's'}
              {' '}
              in your CP account on this screen.
            </Typography>
          </Box>
        )}
      />
    </>
  );
}

ImportPBTemplatesModal.propTypes = {
  classes: PropTypes.object.isRequired,
  currentTemplateCount: PropTypes.number.isRequired,
};

export default withStyles(ImportPBTemplatesModal, importPBTemplatesModalStyle);
